import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Input, Select, Row, Col } from 'antd';
import { GetCreditCardImage, detectCardType } from '@utils';
import { validatePaymentField as schemaValidate } from '@share/schema-validation';
import { insertSpacesInNumber, hideNumbers, creatingArrayWithNumber } from '@share/utils';
import { MasterCardSvg, MaestroCardSvg, VisaCardSvg, AmexCardSvg } from '@assets';
import './styles.scss';

const { Text } = Typography;
const { Option } = Select;

const CARD_MASK_LENGTH = 8;
const CARD_MASK_START = 5;

export interface PaymentFormData {
    nameOnCard: string;
    cardNumber: string;
    expiryMonth: string;
    expiryYear: string;
    securityCode: string;
    cardType: string;
}

interface PaymentFormProps {
    data?: Partial<PaymentFormData>;
    onChange?: (data: PaymentFormData, isValid: boolean) => void;
}

export const PaymentForm: React.FC<PaymentFormProps> = React.memo(({ data, onChange }) => {
    const [formData, setFormData] = useState<PaymentFormData>({
        nameOnCard: data?.nameOnCard || '',
        cardNumber: data?.cardNumber || '',
        expiryMonth: data?.expiryMonth || '',
        expiryYear: data?.expiryYear || '',
        securityCode: data?.securityCode || '',
        cardType: data?.cardType || ''
    });

    const [errors, setErrors] = useState<Partial<Record<keyof PaymentFormData, string>>>({});
    const [cardInputFocused, setCardInputFocused] = useState(false);

    const cardDisplayValue = useMemo(() => {
        const digits = (formData.cardNumber || '').replace(/\D/g, '');
        if (!digits.length) return '';
        const parts = insertSpacesInNumber(digits);
        const spaced = parts ? parts.join(' ') : digits;
        if (cardInputFocused) {
            return spaced;
        }
        return hideNumbers(spaced, creatingArrayWithNumber(CARD_MASK_LENGTH, CARD_MASK_START));
    }, [formData.cardNumber, cardInputFocused]);

    /* Depend on field values, not `data` identity — parent often passes a new object each render. */
    useEffect(() => {
        if (!data) return;
        setFormData(prev => {
            const merged = { ...prev, ...data };
            const digits = (merged.cardNumber || '').replace(/\D/g, '');
            if (digits && !(merged.cardType && String(merged.cardType).trim())) {
                merged.cardType = (detectCardType(digits) as string) || '';
            }
            return merged;
        });
    }, [
        data?.cardNumber,
        data?.nameOnCard,
        data?.expiryMonth,
        data?.expiryYear,
        data?.securityCode,
        data?.cardType,
    ]);

    const validateAll = (currentData: PaymentFormData) => {
        let isValid = true;
        const fields: (keyof PaymentFormData)[] = ['nameOnCard', 'cardNumber', 'expiryMonth', 'expiryYear', 'securityCode'];

        for (const field of fields) {
            if (schemaValidate('paymentMethod', field, currentData[field])) {
                isValid = false;
                break;
            }
        }

        return isValid;
    };

    const handleFieldChange = (field: keyof PaymentFormData, value: any) => {
        let val = value;
        if (field === 'cardNumber') {
            val = (value || '').replace(/\D/g, '').slice(0, 19);
        }
        const newData = { ...formData, [field]: val };
        if (field === 'cardNumber') {
            newData.cardType = (detectCardType(val) as string) || '';
        }
        setFormData(newData);

        const error = schemaValidate('paymentMethod', field, val);
        const newErrors = { ...errors, [field]: error };
        setErrors(newErrors);

        if (onChange) {
            const isValid = validateAll(newData);
            onChange(newData, isValid);
        }
    };

    const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleFieldChange('cardNumber', e.target.value);
    };

    const months = Array.from({ length: 12 }, (_, i) => {
        const month = (i + 1).toString().padStart(2, '0');
        return { label: month, value: month };
    });

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 15 }, (_, i) => {
        const year = (currentYear + i).toString();
        return { label: year, value: year };
    });

    return (
        <div className="activity-payment-form-container">
            <div className="card-payment-logos">
                <div className="payment-method__cards">
                    <MaestroCardSvg />
                    <MasterCardSvg />
                    <VisaCardSvg />
                    <AmexCardSvg />
                </div>
            </div>

            <div className="payment-form">
                <div className="form-item">
                    <Text className="label required">Name on card</Text>
                    <Input
                        placeholder="Name on card"
                        value={formData.nameOnCard}
                        onChange={(e) => handleFieldChange('nameOnCard', e.target.value)}
                        className={`payment-input ${errors.nameOnCard ? 'activity-error-border' : ''}`}
                    />
                    {errors.nameOnCard && <Text className="activity-error-message">{errors.nameOnCard}</Text>}
                </div>

                <div className="form-item">
                    <Text className="label required">Card Number</Text>
                    <div className="card-input-wrapper">
                        <Input
                            placeholder="Card Number"
                            value={cardDisplayValue}
                            onChange={handleCardNumberChange}
                            onFocus={() => setCardInputFocused(true)}
                            onBlur={() => setCardInputFocused(false)}
                            className={`payment-input ${errors.cardNumber ? 'activity-error-border' : ''}`}
                        />
                        <div className="card-type-icon">
                            {GetCreditCardImage(detectCardType(formData.cardNumber) ?? '', !errors.cardNumber)}
                        </div>
                    </div>
                    {errors.cardNumber && <Text className="activity-error-message">{errors.cardNumber}</Text>}
                </div>

                <Row gutter={16}>
                    <Col span={8}>
                        <div className="form-item">
                            <Text className="label required">Exp. Month</Text>
                            <Select
                                placeholder="Month"
                                value={formData.expiryMonth || undefined}
                                onChange={(value) => handleFieldChange('expiryMonth', value)}
                                className={`payment-select ${errors.expiryMonth ? 'activity-error-border' : ''}`}
                                style={{ width: '100%' }}
                            >
                                {months.map(m => (
                                    <Option key={m.value} value={m.value}>{m.label}</Option>
                                ))}
                            </Select>
                        </div>
                    </Col>
                    <Col span={8}>
                        <div className="form-item">
                            <Text className="label required">Exp. Year</Text>
                            <Select
                                placeholder="Year"
                                value={formData.expiryYear || undefined}
                                onChange={(value) => handleFieldChange('expiryYear', value)}
                                className={`payment-select ${errors.expiryYear ? 'activity-error-border' : ''}`}
                                style={{ width: '100%' }}
                            >
                                {years.map(y => (
                                    <Option key={y.value} value={y.value}>{y.label}</Option>
                                ))}
                            </Select>
                        </div>
                    </Col>
                    <Col span={8}>
                        <div className="form-item">
                            <Text className="label required">CVV</Text>
                            <Input
                                placeholder="CVV"
                                type="password"
                                autoComplete="off"
                                value={formData.securityCode}
                                onChange={(e) => handleFieldChange('securityCode', e.target.value)}
                                className={`payment-input ${errors.securityCode ? 'activity-error-border' : ''}`}
                                maxLength={4}
                            />
                            {errors.securityCode && <Text className="activity-error-message">{errors.securityCode}</Text>}
                        </div>
                    </Col>
                </Row>
            </div>
        </div>
    );
});
